import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ApplicationState } from '../../../store/application-store';
import { Observable, interval } from 'rxjs';
import { takeWhile } from 'rxjs';
import * as widgetGallerySelectors from '../../selectors/widget-gallery.selectors';
import * as widgetGalleryActions from '../../actions/widget-gallery.actions';
import { WidgetConfiguration } from '../../../widget-configs/models/widget-configuration';
import * as widgetConfigurationSelectors from '../../../widget-configs/selectors/widget-configs';
import { Direction } from '../../../common/enums/direction';
import { DeviceDetectorService } from '../../../common/device-detection';
import { DashboardScrollService } from '../../../dashboards/services/dashboard-scroll.service';
import { ConfirmationService } from 'primeng/api';


@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mojo-widget-gallery',
  templateUrl: './widget-gallery.component.html'
})
export class WidgetGalleryComponent implements OnInit, OnDestroy {

  private _visible: boolean;
  isAlive = true;

  get visible(): boolean {
    return this._visible;
  }

  @Input()
  set visible(value: boolean) {
    this.resetIdle();
    this._visible = value;
  }

  @Input() idleTimeoutSeconds = 10;

  @Input() timeTillTimeoutSeconds = 10;

  widgetDefinitions$: Observable<Array<WidgetConfiguration>>;

  @Output() widgetClicked = new EventEmitter<WidgetConfiguration>();
  @Output() resetDashboardWidgets = new EventEmitter();

  scrollDirection$: Observable<Direction>;
  scrollDown: Direction = Direction.Down;

  private interval = interval(1000);

  constructor(
    protected store: Store<ApplicationState>,
    private readonly ref: ChangeDetectorRef,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly confirmationService: ConfirmationService,
    dashboardScrollService: DashboardScrollService) {
    this.scrollDirection$ = dashboardScrollService.getScrollDirection();
  }
  
  ngOnInit() {
    this.widgetDefinitions$ = this.store.pipe(select(widgetConfigurationSelectors.selectAll));

    this.store.pipe(
      select(widgetGallerySelectors.selectWidgetGalleryState),
      takeWhile(() => this.isAlive))
      .subscribe((state) => {
        this.visible = state.open;
        this.ref.markForCheck();
      });

    this.interval
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((time) => {
        if (!this.visible) {
          return;
        }
        this.timeTillTimeoutSeconds -= 1;
        if (this.timeTillTimeoutSeconds <= 0) {
          this.store.dispatch(new widgetGalleryActions.Close());
        }
      });
  }

  onHide(event): void {
    this.store.dispatch(new widgetGalleryActions.Close());
  }

  addWidget(definition: WidgetConfiguration) {
    this.widgetClicked.emit(definition);
  }

  resetIdle() {
    this.timeTillTimeoutSeconds = this.idleTimeoutSeconds;
  }

  ngOnDestroy() {
    this.isAlive = false;
    if (this.visible) {
      this.store.dispatch(new widgetGalleryActions.Close());
    }
  }

  isMobile(): boolean {
    return this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
  }

  reset(ignore: Event) {

    this.confirmationService.confirm({
      message: 'This will reset the current dashboard back to the system default layout. Are you sure?',
      header: 'Confirmation',
      accept: () => {
        this.resetDashboardWidgets.emit();
      }
    });

  }

}
